import type { ComponentType, SVGProps } from 'react';
import {
  BookOpen,
  Check,
  Clock3,
  Dumbbell,
  GraduationCap,
  Languages,
  ListTodo,
  MoonStar,
  Target,
  Video
} from 'lucide-react';
import { useFormContext } from 'react-hook-form';
import { Input } from '@/components/ui/field';
import { cn } from '@/lib/cn';
import type { DailyGoals, OnboardingArea, OnboardingData } from './onboarding-schema';

type Icon = ComponentType<SVGProps<SVGSVGElement>>;

interface AreaOption {
  description: string;
  icon: Icon;
  label: string;
  value: OnboardingArea;
}

const AREA_OPTIONS: AreaOption[] = [
  {
    value: 'school',
    label: 'Schule',
    description: 'Lernblöcke und Fächer',
    icon: GraduationCap
  },
  {
    value: 'french',
    label: 'Französisch',
    description: 'A1 Schritt für Schritt',
    icon: Languages
  },
  {
    value: 'sport',
    label: 'Sport',
    description: 'Training und Fortschritt',
    icon: Dumbbell
  },
  {
    value: 'youtube',
    label: 'YouTube',
    description: 'Ideen bis zur Veröffentlichung',
    icon: Video
  },
  {
    value: 'novel',
    label: 'Novel',
    description: 'Kapitel und Story-Arcs',
    icon: BookOpen
  },
  {
    value: 'sleep',
    label: 'Schlaf',
    description: 'Ein stabiler Rhythmus',
    icon: MoonStar
  },
  {
    value: 'personal',
    label: 'Persönliches',
    description: 'Eigene Aufgaben und Routinen',
    icon: ListTodo
  }
];

export function ProfileStep() {
  const {
    formState: { errors },
    register
  } = useFormContext<OnboardingData>();

  return (
    <div className="space-y-5">
      <Input
        autoComplete="given-name"
        error={errors.name?.message}
        label="Wie dürfen wir dich nennen?"
        maxLength={50}
        placeholder="Dein Name"
        {...register('name')}
      />
      <Input
        autoComplete="off"
        description="Ein kurzer Satz genügt. Du kannst dieses Ziel später jederzeit ändern."
        error={errors.primaryGoal?.message}
        label="Was möchtest du vor allem erreichen?"
        maxLength={160}
        placeholder="Zum Beispiel: Meinen Alltag konsequent organisieren"
        {...register('primaryGoal')}
      />
    </div>
  );
}

export function RhythmStep() {
  const {
    formState: { errors },
    register
  } = useFormContext<OnboardingData>();

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <div className="rounded-2xl border border-line bg-elevated/45 p-4">
        <div className="mb-4 flex size-10 items-center justify-center rounded-xl bg-accent/10 text-accent">
          <Clock3 aria-hidden="true" className="size-5" />
        </div>
        <Input
          description="Startpunkt für deinen Tagesplan"
          error={errors.wakeTime?.message}
          label="Gewünschte Aufstehzeit"
          type="time"
          {...register('wakeTime')}
        />
      </div>
      <div className="rounded-2xl border border-line bg-elevated/45 p-4">
        <div className="mb-4 flex size-10 items-center justify-center rounded-xl bg-accent/10 text-accent">
          <MoonStar aria-hidden="true" className="size-5" />
        </div>
        <Input
          description="Zeitpunkt, zu dem der Tag enden soll"
          error={errors.sleepTime?.message}
          label="Gewünschte Schlafenszeit"
          type="time"
          {...register('sleepTime')}
        />
      </div>
      <p className="text-sm leading-relaxed text-muted sm:col-span-2">
        DISCIPLINE plant dazwischen Arbeit, Erholung und Pausen. Beide Zeiten bleiben später
        vollständig bearbeitbar.
      </p>
    </div>
  );
}

export function AreasStep() {
  const {
    formState: { errors },
    register,
    watch
  } = useFormContext<OnboardingData>();
  const selectedAreas = watch('areas');

  return (
    <fieldset aria-describedby={errors.areas ? 'areas-error' : 'areas-hint'}>
      <legend className="sr-only">Wichtige Bereiche</legend>
      <div className="grid gap-3 sm:grid-cols-2">
        {AREA_OPTIONS.map(({ description, icon: IconComponent, label, value }) => {
          const selected = selectedAreas.includes(value);

          return (
            <label
              className={cn(
                'group relative flex min-h-20 cursor-pointer items-center gap-3 rounded-2xl border p-3.5 transition-colors',
                selected
                  ? 'border-accent/55 bg-accent/10'
                  : 'border-line bg-elevated/35 hover:border-muted/50 hover:bg-elevated/70'
              )}
              key={value}
            >
              <input
                className="peer sr-only"
                type="checkbox"
                value={value}
                {...register('areas')}
              />
              <span
                className={cn(
                  'flex size-11 shrink-0 items-center justify-center rounded-xl border transition-colors',
                  selected
                    ? 'border-accent/30 bg-accent text-accent-ink'
                    : 'border-line bg-surface text-muted group-hover:text-ink'
                )}
              >
                <IconComponent aria-hidden="true" className="size-5" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold text-ink">{label}</span>
                <span className="mt-0.5 block text-xs leading-relaxed text-muted">
                  {description}
                </span>
              </span>
              <span
                aria-hidden="true"
                className={cn(
                  'flex size-6 shrink-0 items-center justify-center rounded-full border',
                  selected
                    ? 'border-accent bg-accent text-accent-ink'
                    : 'border-line bg-surface text-transparent'
                )}
              >
                <Check className="size-3.5" strokeWidth={3} />
              </span>
            </label>
          );
        })}
      </div>
      {errors.areas ? (
        <p className="mt-3 text-sm text-danger" id="areas-error">
          {errors.areas.message}
        </p>
      ) : (
        <p className="mt-3 text-sm text-muted" id="areas-hint">
          Wähle alles aus, was in deinem Alltag sichtbar sein soll.
        </p>
      )}
    </fieldset>
  );
}

interface GoalInputProps {
  description: string;
  field: keyof DailyGoals;
  label: string;
  max: number;
  min?: number;
  suffix: string;
}

function GoalInput({ description, field, label, max, min = 1, suffix }: GoalInputProps) {
  const {
    formState: { errors },
    register
  } = useFormContext<OnboardingData>();
  const error = errors.dailyGoals?.[field]?.message;

  return (
    <div className="rounded-2xl border border-line bg-elevated/35 p-4">
      <Input
        description={description}
        error={error}
        inputMode="numeric"
        label={label}
        max={max}
        min={min}
        step={1}
        type="number"
        {...register(`dailyGoals.${field}`, { valueAsNumber: true })}
      />
      <p aria-hidden="true" className="mt-2 text-xs font-medium text-muted">
        {suffix}
      </p>
    </div>
  );
}

export function GoalsStep() {
  return (
    <div className="space-y-6">
      <section aria-labelledby="time-goals-heading">
        <div className="mb-3 flex items-center gap-2">
          <Target aria-hidden="true" className="size-4 text-accent" />
          <h3 className="text-sm font-semibold text-ink" id="time-goals-heading">
            Fokuszeit pro Tag
          </h3>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <GoalInput
            description="Konzentrierte Lernzeit"
            field="schoolMinutes"
            label="Schule"
            max={720}
            suffix="Minuten"
          />
          <GoalInput
            description="Vokabeln, Grammatik und Praxis"
            field="frenchMinutes"
            label="Französisch"
            max={720}
            suffix="Minuten"
          />
          <GoalInput
            description="Produktion statt Konsum"
            field="youtubeMinutes"
            label="YouTube"
            max={720}
            suffix="Minuten"
          />
        </div>
      </section>

      <section aria-labelledby="output-goals-heading">
        <div className="mb-3 flex items-center gap-2">
          <BookOpen aria-hidden="true" className="size-4 text-accent" />
          <h3 className="text-sm font-semibold text-ink" id="output-goals-heading">
            Kreatives Ziel
          </h3>
        </div>
        <GoalInput
          description="Geplante oder geschriebene Kapitel"
          field="novelChapters"
          label="Novel-Kapitel"
          max={100}
          suffix="Kapitel pro Tag"
        />
      </section>

      <section aria-labelledby="sport-goals-heading">
        <div className="mb-3 flex items-center gap-2">
          <Dumbbell aria-hidden="true" className="size-4 text-accent" />
          <h3 className="text-sm font-semibold text-ink" id="sport-goals-heading">
            Sport
          </h3>
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          <GoalInput
            description="Saubere Wiederholungen am Stück"
            field="pushUpCurrentMax"
            label="Aktuelles Push-up-Maximum"
            max={1_000}
            min={0}
            suffix="Wiederholungen"
          />
          <GoalInput
            description="Dein langfristiger Orientierungspunkt"
            field="pushUpLongTermGoal"
            label="Langfristiges Push-up-Ziel"
            max={2_000}
            suffix="am Stück"
          />
          <GoalInput
            description="Gesamtes Tagesvolumen"
            field="squatsDaily"
            label="Kniebeugen"
            max={2_000}
            suffix="pro Tag"
          />
        </div>
      </section>
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-line/70 py-3 last:border-0">
      <dt className="text-sm text-muted">{label}</dt>
      <dd className="max-w-[65%] text-right text-sm font-semibold text-ink">{value}</dd>
    </div>
  );
}

export function SummaryStep() {
  const { getValues } = useFormContext<OnboardingData>();
  const values = getValues();
  const areaLabels = values.areas
    .map((area) => AREA_OPTIONS.find((option) => option.value === area)?.label)
    .filter((label): label is string => Boolean(label));

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <section
        aria-labelledby="summary-profile-heading"
        className="rounded-2xl border border-line bg-elevated/35 p-4"
      >
        <h3 className="text-sm font-semibold text-ink" id="summary-profile-heading">
          Dein Rahmen
        </h3>
        <dl className="mt-2">
          <SummaryRow label="Name" value={values.name} />
          <SummaryRow label="Aufstehen" value={`${values.wakeTime} Uhr`} />
          <SummaryRow label="Schlafen" value={`${values.sleepTime} Uhr`} />
        </dl>
        <div className="mt-3 rounded-xl border border-accent/20 bg-accent/10 p-3">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-accent">
            Wichtigstes Ziel
          </p>
          <p className="mt-1.5 text-sm leading-relaxed text-ink">{values.primaryGoal}</p>
        </div>
      </section>

      <section
        aria-labelledby="summary-areas-heading"
        className="rounded-2xl border border-line bg-elevated/35 p-4"
      >
        <h3 className="text-sm font-semibold text-ink" id="summary-areas-heading">
          Deine Bereiche
        </h3>
        <ul className="mt-3 flex flex-wrap gap-2" aria-label="Ausgewählte Bereiche">
          {areaLabels.map((label) => (
            <li
              className="rounded-full border border-line bg-surface px-3 py-1.5 text-xs font-medium text-ink"
              key={label}
            >
              {label}
            </li>
          ))}
        </ul>
      </section>

      <section
        aria-labelledby="summary-goals-heading"
        className="rounded-2xl border border-line bg-elevated/35 p-4 md:col-span-2"
      >
        <h3 className="text-sm font-semibold text-ink" id="summary-goals-heading">
          Deine täglichen Ziele
        </h3>
        <dl className="mt-2 grid gap-x-6 sm:grid-cols-2">
          <SummaryRow label="Schule" value={`${values.dailyGoals.schoolMinutes} Min.`} />
          <SummaryRow label="Französisch" value={`${values.dailyGoals.frenchMinutes} Min.`} />
          <SummaryRow label="YouTube" value={`${values.dailyGoals.youtubeMinutes} Min.`} />
          <SummaryRow label="Novel" value={`${values.dailyGoals.novelChapters} Kapitel`} />
          <SummaryRow
            label="Push-up-Maximum → Ziel"
            value={`${values.dailyGoals.pushUpCurrentMax} → ${values.dailyGoals.pushUpLongTermGoal}`}
          />
          <SummaryRow label="Kniebeugen" value={`${values.dailyGoals.squatsDaily} pro Tag`} />
        </dl>
      </section>
    </div>
  );
}
